/* Copyright (c) 2020-present CloudIO Inc */

import { toOptions } from 'componentregistry/utils/ManagerUtils';
import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { AliasType, ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './CalendarHeatmap';
import { helpRenders } from './CalendarHeatmap_Help';

const CalendarHeatmapManager: ComponentManagerFn<'CalendarHeatmap'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'CalendarHeatmap'>(platform, 'CalendarHeatmap'),
    type: 'CalendarHeatmap',
    name: 'Calendar Heatmap',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Data',
        allowedTypes: ['CloudDataStore', 'LocalDataStore', 'ReadOnlyDataStore'],
      },
      dateAttribute: {
        label: 'Date Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['Date'],
        preventMultiEdit: true,
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ViewAttributeChooser',
        category: 'Data',
        aliasType: 'LOV',
        getAttributeDataTypes: () => ['Integer', 'Double', 'Decimal'],
        preventMultiEdit: true,
      },
      groupFunction: {
        label: 'Group Function',
        type: 'select',
        options: toOptions(['Avg', 'Count', 'Max', 'Min', 'Sum']),
        required: true,
        category: 'Data',
      },
      showMonthLabels: {
        label: 'Show Month Labels',
        type: 'boolean',
        category: 'Labels',
      },
      showWeekdayLabels: {
        label: 'Show Weekday Labels',
        type: 'boolean',
        category: 'Labels',
      },
      showOutOfRangeDays: {
        label: 'Show Out Of Range Days',
        type: 'boolean',
      },
      color1MaxValue: {
        label: 'Color 1 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the first color to be used in the heatmap.',
      },
      color2MaxValue: {
        label: 'Color 2 Max Value',
        type: 'numberInput',
        required: true,
        category: 'Color',
        helpText:
          'Enter a value upto which the 2nd color color to be used in the heatmap.',
      },
      color3MaxValue: {
        label: 'Color 3 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the 3rd color color to be used in the heatmap.',
      },
      color4MaxValue: {
        label: 'Color 4 Max Value',
        type: 'numberInput',
        category: 'Color',
        required: true,
        helpText:
          'Enter a value upto which the last color to be used in the heatmap. Every other value higher than this will be shown as black',
      },
      fromDateRange: {
        label: 'From Date Range',
        type: 'number',
        category: 'Data',
        min: -365,
        max: 0,
        step: 7,
        required: true,
        helpText:
          'Enter the number of days to go back for the range. e.g. -365 to show for last one year.',
      },
      toDateRange: {
        label: 'To Date Range',
        type: 'number',
        category: 'Data',
        min: 0,
        max: 365,
        step: 7,
        required: true,
        helpText:
          'Enter 0 to show till today or any positive number to show in the future.',
      },
      toolTip: {
        label: 'Tooltip',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
    },
    isPropertyVisible(prop, itemData): boolean {
      switch (prop) {
        case 'dateAttribute':
        case 'valueAttribute':
        case 'groupFunction':
          return isNotEmpty(itemData.comboDatasourceAlias);
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDisplayText: (item) => item.props.dateAttribute,
    category: 'Dashboard',
    icon: { iconName: 'calendar-alt' },
    description: `The Calendar Heatmap component visualizes values over days in a calendar-like view, making it easy to identify daily patterns or anomalies. 

\`\`\`alert
Note: Make sure to specify different values for date & count attributes
\`\`\`
`,
    helpRenders,
  };
};

export default CalendarHeatmapManager;
